import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Panel, PrimaryButton, GhostButton } from "@/components/kit";
import { Check, Loader2, CreditCard, Users, Sparkles, RefreshCw } from "lucide-react";

const money = (cents) => `$${(cents / 100).toFixed(0)}`;

const STATUS_TONE = {
  active: "text-emerald-400 border-emerald-500/30 bg-emerald-500/10",
  trialing: "text-sky-400 border-sky-500/30 bg-sky-500/10",
  past_due: "text-amber-400 border-amber-500/30 bg-amber-500/10",
  canceled: "text-rose-400 border-rose-500/30 bg-rose-500/10",
};

export default function Billing() {
  const { refreshCompany } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/billing/plans");
      setData(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); refreshCompany?.(); }, []); // eslint-disable-line

  const subscribe = async (lookup_key) => {
    setBusy(lookup_key);
    try {
      const { data } = await api.post("/billing/checkout", { lookup_key, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy("");
    }
  };

  const manageBilling = async () => {
    setBusy("portal");
    try {
      const { data } = await api.post("/billing/portal", { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setBusy("");
    }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-orange-500" /></div>;

  const company = data?.company;
  const plans = data?.plans || [];
  const members = data?.member_count ?? 0;
  const limit = company?.user_limit;
  const status = company?.subscription_status;

  return (
    <div className="space-y-6" data-testid="billing-page">
      <PageHeader
        title="Billing & Plan"
        subtitle="Manage your subscription, seats and payment method."
        actions={
          <div className="flex gap-2">
            <GhostButton onClick={load} data-testid="billing-refresh"><RefreshCw className="h-4 w-4" /> Refresh</GhostButton>
            {company?.has_billing && (
              <PrimaryButton onClick={manageBilling} disabled={busy === "portal"} data-testid="billing-portal-button">
                {busy === "portal" ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Manage billing
              </PrimaryButton>
            )}
          </div>
        }
      />

      <Panel className="p-5" data-testid="billing-current">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <div className="font-mono text-[11px] uppercase tracking-wider text-slate-500">Current plan</div>
            <div className="flex items-center gap-3 mt-1">
              <span className="font-heading text-2xl font-extrabold uppercase text-slate-100" data-testid="billing-current-plan">{company?.plan_label}</span>
              {status && <span className={`text-[11px] font-mono uppercase px-2 py-0.5 rounded-full border ${STATUS_TONE[status] || STATUS_TONE.canceled}`}>{String(status).replace(/_/g, " ")}</span>}
            </div>
            {status === "trialing" && company?.trial_ends_at && (
              <div className="text-xs text-slate-400 mt-1">Trial ends {new Date(company.trial_ends_at).toLocaleDateString()}</div>
            )}
          </div>
          <div className="flex items-center gap-2 text-slate-300">
            <Users className="h-5 w-5 text-orange-400" />
            <span className="font-mono text-lg" data-testid="billing-seat-usage">{members}{limit != null ? ` / ${limit}` : ""}</span>
            <span className="text-xs text-slate-500">{limit != null ? "seats used" : "users (unlimited)"}</span>
          </div>
        </div>
      </Panel>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="billing-plans">
        {plans.map((p) => {
          const isCurrent = company?.plan === p.key;
          return (
            <Panel key={p.key} className={`p-5 flex flex-col ${isCurrent ? "border-orange-500/60 ring-1 ring-orange-500/30" : ""}`} data-testid={`plan-card-${p.key}`}>
              <div className="flex items-center justify-between">
                <div className="font-heading text-xl font-bold uppercase tracking-wide text-slate-100">{p.label}</div>
                {p.key === "professional" && <Sparkles className="h-4 w-4 text-orange-400" />}
              </div>
              <div className="mt-2"><span className="font-mono text-3xl font-extrabold text-slate-100">{money(p.amount)}</span><span className="text-slate-500 text-sm">/mo</span></div>
              <p className="text-sm text-slate-400 mt-2">{p.description}</p>
              <ul className="mt-4 space-y-2 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-300"><Check className="h-4 w-4 text-emerald-400 mt-0.5 flex-shrink-0" /> {f}</li>
                ))}
              </ul>
              <div className="mt-5">
                {isCurrent ? (
                  <div className="text-center text-xs font-mono uppercase tracking-wider text-orange-400 border border-orange-500/30 rounded-md py-2" data-testid={`plan-current-${p.key}`}>Current plan</div>
                ) : (
                  <PrimaryButton className="w-full" onClick={() => subscribe(p.lookup_key)} disabled={!!busy} data-testid={`plan-subscribe-${p.key}`}>
                    {busy === p.lookup_key ? <Loader2 className="h-4 w-4 animate-spin" /> : <CreditCard className="h-4 w-4" />} Subscribe
                  </PrimaryButton>
                )}
              </div>
            </Panel>
          );
        })}
      </div>

      <p className="text-xs text-slate-500">Payments are processed securely by Stripe. Use test card 4242 4242 4242 4242 with any future expiry and CVC while in test mode.</p>
    </div>
  );
}
